import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Call, StructuredData } from "@/api/calls/calls.types"
import { 
    BarChart3, 
    Copy, 
    Check, 
    FileText, 
    CheckCircle2, 
    XCircle,
    Database 
} from "lucide-react"
import { useState } from "react"

interface AnalysisTabProps {
    call: Call
}

const useClipboard = () => {
    const [copied, setCopied] = useState<string | null>(null);
    
    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(null), 2000);
    };

    return { copied, copyToClipboard };
};

export function AnalysisTab({ call }: AnalysisTabProps) {
    const { copied, copyToClipboard } = useClipboard();

    // Early return if no analysis data
    const hasSummary = call?.analysis?.summary;
    const hasSuccessEvaluation = call?.analysis?.successEvaluation !== null && call?.analysis?.successEvaluation !== undefined;
    const hasStructuredData = call?.analysis?.structuredData && Object.keys(call.analysis.structuredData).length > 0;
    
    if (!call?.analysis || (!hasSummary && !hasSuccessEvaluation && !hasStructuredData)) {
        return (
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <BarChart3 className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Analysis</CardTitle>
                            <CardDescription className="text-xs text-muted-foreground/70">
                                No analysis available
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                            <BarChart3 className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-base font-semibold mb-2">No Analysis Available</h3>
                        <p className="text-sm text-muted-foreground max-w-sm">
                            No call analysis data is available for this conversation.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Card */}
            {call?.analysis?.summary && (
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <FileText className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Summary</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">
                                        AI-generated call summary
                                    </CardDescription>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(call.analysis?.summary || '', 'summary')}
                                className="h-8 w-8"
                            >
                                {copied === 'summary' ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <p className="text-sm leading-relaxed">
                            {call.analysis.summary}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Success Evaluation Card */}
            {call?.analysis?.successEvaluation !== null && call?.analysis?.successEvaluation !== undefined && (
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    {typeof call.analysis.successEvaluation === 'boolean' ? (
                                        call.analysis.successEvaluation ? (
                                            <CheckCircle2 className="h-4 w-4 text-primary" />
                                        ) : (
                                            <XCircle className="h-4 w-4 text-primary" />
                                        )
                                    ) : (
                                        <CheckCircle2 className="h-4 w-4 text-primary" />
                                    )}
                                </div>
                                <div>
                                    <CardTitle className="text-base">Success Evaluation</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">
                                        Call outcome assessment
                                    </CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {typeof call.analysis.successEvaluation === 'boolean' && (
                                    <Badge 
                                        variant={call.analysis.successEvaluation ? "default" : "secondary"}
                                        className="gap-1.5"
                                    >
                                        {call.analysis.successEvaluation ? (
                                            <>
                                                <CheckCircle2 className="h-3 w-3" />
                                                Successful
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="h-3 w-3" />
                                                Unsuccessful
                                            </>
                                        )}
                                    </Badge>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => copyToClipboard(
                                        typeof call.analysis?.successEvaluation === 'object' 
                                            ? JSON.stringify(call.analysis.successEvaluation, null, 2)
                                            : String(call.analysis?.successEvaluation || ''),
                                        'evaluation'
                                    )}
                                    className="h-8 w-8"
                                >
                                    {copied === 'evaluation' ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        {typeof call.analysis.successEvaluation === 'boolean' ? (
                            <p className="text-sm text-muted-foreground">
                                {call.analysis.successEvaluation 
                                    ? "This call was evaluated as successful based on the configured success criteria."
                                    : "This call did not meet the configured success criteria."}
                            </p>
                        ) : typeof call.analysis.successEvaluation === 'string' ? (
                            <p className="text-sm leading-relaxed">
                                {call.analysis.successEvaluation}
                            </p>
                        ) : typeof call.analysis.successEvaluation === 'object' ? (
                            <ScrollArea className="h-auto max-h-[300px]">
                                <pre className="text-sm p-3 rounded-lg bg-muted/30 border overflow-x-auto">
                                    {JSON.stringify(call.analysis.successEvaluation, null, 2)}
                                </pre>
                            </ScrollArea>
                        ) : (
                            <p className="text-sm leading-relaxed">
                                {String(call.analysis.successEvaluation)}
                            </p>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Structured Data Card */}
            {call?.analysis?.structuredData && Object.keys(call.analysis.structuredData).length > 0 && (
                <Card className="shadow-sm">
                    <CardHeader className="border-b bg-muted/20">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                                    <Database className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Structured Data</CardTitle>
                                    <CardDescription className="text-xs text-muted-foreground/70">
                                        Extracted data fields
                                    </CardDescription>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(JSON.stringify(call.analysis?.structuredData, null, 2), 'structured')}
                                className="h-8 w-8"
                            >
                                {copied === 'structured' ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="space-y-3">
                            {Object.entries(call.analysis.structuredData).map(([key, value]) => (
                                <div key={key} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-muted-foreground mb-1">
                                            {key}
                                        </div>
                                        <div className="text-sm break-words">
                                            {typeof value === 'string' 
                                                ? value 
                                                : JSON.stringify(value, null, 2)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
